import {
  isValidElement, memo, ReactNode, useCallback, useEffect, useMemo, useState, forwardRef,
  useImperativeHandle
} from 'react'

import './index.scss'
import Row from './Row'
import Input from '../Input'
import Icon from '../Icon'
import { useTranslation } from 'react-i18next'
import { ICON } from '../Icon/data'
import i18n from '../../i18n'
import LoadingBar from '../LoadingBar'
import { renderToString } from 'react-dom/server'
import { isValue } from '../../services/utils/misc'
import useDynamicRef from '../../hooks/useDynamicRef'

type Width = {
  min: string
  max: string
}

export type TableHeader = {
  value?: string
  formatFunction?: (val: string, data: any) => string | ReactNode
  headerFormatFunction?: (val: string, data: any) => string | ReactNode
  isPinned?: boolean
  index?: number
  class?: string[]
  width?: string | Width
  disableSort?: boolean
  headerElemSize?: 'l' | 'm' | 's'
}

export type TableSort = {
  column: string
  type: 'ascending' | 'descending'
}

type Table = {
  tableId?: string
  showSearch?: boolean
  tableHeader: Record<string, TableHeader>
  rows?: Record<string, string>
  rowHeight?: number
  headerRowHeight?: number
  includePageFooter?: boolean
  pageSizeArr?: number[]
  headerElems?: ReactNode[]
  title?: string | ReactNode
  description?: string
  emptyTableContent?: string | ReactNode
  isExpandable?: boolean
  autoExpand?: boolean
  isLoading?: boolean
  onRowClick?: (row: any) => void
  defaultSort?: TableSort
  headerElemSize?: 's' | 'm' | 'l'
}

const Table = memo(forwardRef(({
  showSearch = true, tableHeader = {}, rows = [], rowHeight = 50, headerRowHeight, title,
  includePageFooter = true, pageSizeArr = [5, 10, 20, 50, 100], headerElems,
  description, emptyTableContent = i18n.t('No data to show'), isExpandable = false,
  autoExpand = true, isLoading = false, tableId, onRowClick, defaultSort, headerElemSize = 'm'
}: Table, ref) => {
  const { t } = useTranslation()

  const [search, setSearch] = useState<string>('')
  const [pageSize, setPageSize] = useState<number>(pageSizeArr[0])
  const [pageStatus, setPageStatus] = useState<number>(1)
  const [sort, setSort] = useState<TableSort | null>(defaultSort ?? null)

  const header = useMemo(() => {
    if (isExpandable) {
      return ({
        EXPAND: {
          id: 'EXPAND',
          label: '',
          index: -1,
          disableSort: true,
        },
        ...tableHeader
      })
    }
    return tableHeader
  }, [isExpandable, tableHeader])

  const filteredRows = useMemo(() => {
    const rowArr = rows
      .sort((a, b) => {
        if (!sort) return 0
        const { column, type } = sort
        if (a[column] === b[column]) return 0
        const typeMult = type === 'ascending' ? 1 : -1
        return a[column] > b[column] ? typeMult * 1 : typeMult * -1
      })
      .map((row, idx) => {
        const newRow = { ...row }
        if (!newRow.rowId) newRow.rowId = idx
        const searchString: string[] = []
        Object.keys(header).forEach((key) => {
          const { formatFunction } = header[key]
          if (newRow[key]) searchString.push(newRow[key])
          if (formatFunction) {
            const value = newRow[key]
            const formatValue = formatFunction(value, newRow)
            if (isValidElement(formatValue) && value !== formatValue) searchString.push(newRow[key])
            else {
              const elementStr = renderToString(formatValue)
              const div = document.createElement('div')
              div.innerHTML = elementStr
              const text = div.textContent ?? div.innerText ?? ''
              if (isValue(text) && text !== value) searchString.push(text)
            }
          }
        })

        newRow.search_string = searchString.join('_')
        return newRow
      })
    
    if (isLoading) {
      let count = pageSize
      const rowArr = []
      while (count !== 0) {
        const row = {}
        Object.keys(header).forEach((id) => {
          row[id] = <LoadingBar />
        })
        rowArr.push(row)
        count = count - 1
      }
      return rowArr
    }
    
    if (!search) return rowArr

    return rowArr
      .filter((row) => row.search_string.toLowerCase().includes(search.toLocaleLowerCase()))
  }, [rows, search, header, isLoading, pageSize, sort])

  const onSort = useCallback((key: string) => {
    if (key !== sort?.column) setSort({ column: key, type: 'ascending' })
    else if (key === sort?.column) {
      const type = sort?.type === 'ascending' ? 'descending' : 'ascending'
      setSort({ column: key, type })
    }
  }, [sort])

  useEffect(() => {
    setPageStatus(1)
  }, [pageSize, search])

  const pageArr = useMemo(() => {
    const rowSize = filteredRows.length
    const arrOptions = {}
    const arraySize = Math.ceil(rowSize / pageSize)
    for (let i = 1; i < arraySize + 1; i++) {
      arrOptions[i] = i
    }
    return arrOptions
  }, [filteredRows, pageSize])

  const pageIndex = useMemo(() => {
    let max = pageSize * pageStatus
    let min = max - (pageSize - 1)
    if (max > filteredRows.length) max = filteredRows.length
    return { min, max }
  }, [pageStatus, pageSize, filteredRows])

  const pagedRows = useMemo(() => {
    if (includePageFooter) {
      return [...filteredRows].splice(pageIndex.min - 1, pageSize)
    }
    return [...filteredRows]
  }, [filteredRows, pageIndex, includePageFooter, pageSize])

  const tableStyle = useMemo(() => {
    const styleObj = {}
    if (emptyTableContent && filteredRows.length === 0) {
      styleObj.height = `${(2 * (rowHeight + 2)) + 3}px`
    } else if (includePageFooter) {
      styleObj.height = `${((pageSize + 1) * (rowHeight + 2)) + 3}px`
    }
    return styleObj
  }, [pageSize, rowHeight, includePageFooter, filteredRows])

  const flipPage = useCallback((offset = 1) => {
    const newStatus = offset + parseInt(pageStatus)
    const maxIndex = Object.keys(pageArr).length
    if (newStatus >= 1 && newStatus <= maxIndex) {
      setPageStatus(newStatus)
    }
  }, [pageStatus, pageArr])

  const useFiller = useMemo(() => {
    return pagedRows.length % pageSize !== 0
  }, [pagedRows, pageSize])

  const forwardRefAction = useDynamicRef({
    setSort,
  })
  useImperativeHandle(ref, () => forwardRefAction, [forwardRefAction])

  return (
    <div className="Table">
      {(title || description) && (
        <div className="Table__title">
          {title && (<div className="Table__title-header">{title}</div>)}
          {description && (<div className="Table__title-descr">{description}</div>)}
        </div>
      )}
      {(showSearch || headerElems) && (
        <div className="Table__header">
          {showSearch && (
            <div className="Table__search">
              <Input
                type="text"
                size={headerElemSize}
                value={search}
                onInput={(val) => setSearch(val)}
              />
              <Icon icon={ICON.SEARCH} color="#6F6F6F" />
            </div>
          )}
          {headerElems && (
            <div className="Table__header-customElems">
              {headerElems?.map((elem) => (
                <div className="Table__header-customElems__elem">{elem}</div>
              ))}
            </div>
          )}
        </div>
      )}
      <div id={`${tableId}_container`} className="Table__table">
        <table id={tableId} style={tableStyle}>
          <thead>
            <Row
              isHeader={true}
              data={tableHeader}
              header={header}
              rowHeight={headerRowHeight ?? rowHeight}
              isLoading={isLoading}
              sort={sort}
              onSort={(key: string) => onSort(key)}
            />
          </thead>
          <tbody>
            {pagedRows.map((row) => {
              return (
                <Row
                  id={row?.rowId}
                  data={row}
                  header={header}
                  rowHeight={rowHeight}
                  autoExpand={autoExpand}
                  isLoading={isLoading}
                  onRowClick={onRowClick}
                />
              )
            })}
            {useFiller && (
              <Row isFiller={true} />
            )}
            {!pagedRows.length && (
              <tr className="Table__emptycontent">
                <td style={{ height: rowHeight }}>{emptyTableContent}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {includePageFooter && (
        <div className="Table__footer">
          <div className="Table__footer-left">
            <div className="Table__footer-options">
              <Input
                type="select"
                options={pageSizeArr.reduce((obj: { [key: number]: number }, val: number) => {
                  obj[val] = val
                  return obj
                }, {})}
                value={pageSize}
                onInput={(val) => setPageSize(parseInt(val))}
              />
            </div>
            <div className="Table__footer-status">
              {`${pageIndex.min} - ${pageIndex.max} ${t('of {{rowCount}} items', { rowCount: filteredRows.length })}`}
            </div>
          </div>
          <div className="Table__footer-right">
            <div className="Table__footer-pages">
              <Input
                type="select"
                options={pageArr}
                value={pageStatus}
                onInput={(val) => setPageStatus(parseInt(val))}
              />
              {t('of {{pageCount}} pages', { pageCount: Object.keys(pageArr).length })}
            </div>
            <div className="Table__footer-pageFlip">
              <div
                className="Table__footer-pageFlip__back"
                role="button"
                tabIndex={0}
                onClick={() => flipPage(-1)}
              >
                <Icon icon={ICON.CARET_LEFT} />
              </div>
              <div
                className="Table__footer-pageFlip__next"
                role="button"
                tabIndex={0}
                onClick={() => flipPage(1)}
              >
                <Icon icon={ICON.CARET_RIGHT} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}))

export default Table
